import { isNil, mapValues, sumBy } from "lodash";
import { isEmpty } from "underscore";
import { GetVehicleAssignmentsMyFleetQuery, GetVehicleAssignmentsQuery } from "../../api/parkingProducts.api";
import {
  AssignedParkingRight,
  VehicleAssignment,
  VehicleAssignmentMyFleet,
  VehicleAssignmentProduct,
  VehicleAssignmentProductMyFleet,
} from "../../models/delegations/DelegatedParkingProductsModel";
import { LocatedParkingProduct } from "../../models/LocatedParkingProduct";
import { AssignedVehicleSuccessfulMutation } from "../../models/vehicle-assignments/AssignedVehicleSuccessfulMutation";
import { AssignVehicleRequestModel } from "../../models/vehicle-assignments/AssignVehicleRequestModel";
import { ParkingRightItemModel } from "../../models/vehicle-assignments/ParkingRightItemModel";
import { RevokeVehicleAssignmentsModel } from "../../models/vehicle-assignments/RevokeVehicleAssignmentsModel";
import { UpdateVehicleRequestModel } from "../../models/vehicle-assignments/UpdateVehicleRequestModel";
import {
  ParkingProductState,
  VehiclesMyProductsState as VehicleAssignmentMyProductsState,
  VehicleAssignmentsMyFleetOverviewState,
  VehicleAssignmentsMyFleetState,
} from "../reducers/parking-products";

function reduceUpdate(
  state: ParkingProductState,
  aggregateId: string,
  update: UpdateVehicleRequestModel,
  pmcToParkingRightStartDates: Record<number, ParkingRightItemModel>
): ParkingProductState {
  const revokedPmcIds = state.selectedVehicleAssignment
    .data!.assignedProducts.map((x) => x.pmcId)
    .filter((assignedPmc) => !update.parkingProducts.map(p => p.pmcId).includes(assignedPmc));
  const assignedParkingProducts = update.parkingProducts.filter(
    (parkingProduct) =>
      !state.selectedVehicleAssignment.data!.assignedProducts.some(
        (assignedProduct) => assignedProduct.pmcId === parkingProduct.pmcId
      )
  );

  const newLocatedParkingProducts = incrementTotalAssignedCount(
    state.locatedParkingProducts.data!,
    Object.fromEntries([
      ...revokedPmcIds.map((revokedPmc) => [revokedPmc, -1]),
      ...assignedParkingProducts.map((assignedPmcId) => {
        if(assignedPmcId.activationDate && new Date(assignedPmcId.activationDate) > new Date()){
          return [assignedPmcId.pmcId, 0];
        }
        return [assignedPmcId.pmcId, 1];  
      }),
    ])
  );

  const newMyProductsVehicles = reduceMyProductsVehiclesState(
    state.vehiclesMyProducts,
    state.locatedParkingProducts.data!,
    aggregateId,
    update,
    pmcToParkingRightStartDates
  );

  const newMyFleetVehicles = reduceMyFleetVehiclesStateUpdate(
    state.vehicleAssignmentsMyFleet,
    state.locatedParkingProducts.data!,
    aggregateId,
    update
  );

  const newMyFleetOverviewVehicles = reduceMyFleetOverviewVehiclesStateUpdate(
    state.vehicleAssignmentsMyFleetOverview,
    state.locatedParkingProducts.data!,
    aggregateId,
    update
  );

  return {
    ...state,
    locatedParkingProducts: {
      ...state.locatedParkingProducts,
      data: newLocatedParkingProducts,
    },
    vehiclesMyProducts: newMyProductsVehicles,
    vehicleAssignmentsMyFleet: newMyFleetVehicles,
    vehicleAssignmentsMyFleetOverview: newMyFleetOverviewVehicles,
  };
}

export function incrementTotalAssignedCount(
  locations: LocatedParkingProduct[],
  pmcIdToAmount: { [pmcId: number]: number }
): LocatedParkingProduct[] {
  return locations.map((location): LocatedParkingProduct => {
    const newParkingProducts = location.parkingProducts.map((parkingProduct) => {
      const amount = pmcIdToAmount[parkingProduct.pmcId!];
      if (amount) {
        return {
          ...parkingProduct,
          totalAssignedParkingRights: parkingProduct.totalAssignedParkingRights! + amount,
        };
      }
      return parkingProduct;
    });

    const totalAssignedParkingRights = sumBy(newParkingProducts, x => x.totalAssignedParkingRights!);

    return {
      ...location,
      totalAssignedParkingRights,
      parkingProducts: newParkingProducts,
    };
  });
}

function hasQueryValue(value: string | number[] | undefined): boolean {
  return !isNil(value) && !isEmpty(value);
}

function matchesTextFilter(
  queryValue: string | undefined,
  actualValue: string | null | undefined
): boolean {
  if (!hasQueryValue(queryValue)) {
    return true;
  }

  if (isNil(actualValue)) {
    return false;
  }

  return actualValue.toLowerCase().includes(queryValue!.toLowerCase());
}

function matchesPmcFilter(queryPmcIds: number[] | undefined, actualPmcId: number): boolean {
  return !hasQueryValue(queryPmcIds) || queryPmcIds!.includes(actualPmcId);
}

function matchesAnyPmcFilter(queryPmcIds: number[] | undefined, actualPmcIds: number[]): boolean {
  return !hasQueryValue(queryPmcIds) || actualPmcIds.some((pmcId) => queryPmcIds!.includes(pmcId));
}

function matchesDateRangeFilter(
  value: Date | string | null | undefined,
  start: string | undefined,
  end: string | undefined
): boolean {
  const timestamp = isNil(value) ? undefined : new Date(value).getTime();

  return (
    (isNil(start) || (!isNil(timestamp) && new Date(start).getTime() <= timestamp)) &&
    (isNil(end) || (!isNil(timestamp) && timestamp <= new Date(end).getTime()))
  );
}

function vehicleAssignmentMatchesQuery(
  vehicleAssignment: VehicleAssignment,
  query: GetVehicleAssignmentsQuery
): boolean {
  const matchesDescription = matchesTextFilter(
    query.description,
    vehicleAssignment.vehicle.description
  );
  const matchesNumberPlate = matchesTextFilter(
    query.numberPlateValue,
    vehicleAssignment.vehicle.identifier
  );
  const matchesProducts = matchesAnyPmcFilter(
    query.pmcIds,
    vehicleAssignment.products.map((product) => product.pmcId)
  );
  const matchesAssignedDate = matchesDateRangeFilter(
    vehicleAssignment.assignedAt,
    query.assignedDateStart,
    query.assignedDateEnd
  );

  return (
    matchesDescription &&
    matchesNumberPlate &&
    matchesProducts &&
    matchesAssignedDate
  );
}

function assignedParkingRightMatchesQuery(
  assignedParkingRight: AssignedParkingRight,
  query: GetVehicleAssignmentsQuery,
  getPlaceId: (pmc: number) => number
): boolean {
  const matchesPlace = query.placeId === getPlaceId(assignedParkingRight.parkingRight.pmc);
  const matchesDescription = matchesTextFilter(
    query.description,
    assignedParkingRight.vehicle.description
  );
  const matchesNumberPlate = matchesTextFilter(
    query.numberPlateValue,
    assignedParkingRight.vehicle.identifier
  );
  const matchesProducts = matchesPmcFilter(query.pmcIds, assignedParkingRight.parkingRight.pmc);
  const matchesAssignedDate = matchesDateRangeFilter(
    assignedParkingRight.assigned,
    query.assignedDateStart,
    query.assignedDateEnd
  );
  const matchesStartedDate = matchesDateRangeFilter(
    assignedParkingRight.startedAt,
    query.startedDateStart,
    query.startedDateEnd
  );
  const matchesActivationDate = matchesDateRangeFilter(
    assignedParkingRight.activationDate,
    query.activationDateStart,
    query.activationDateEnd
  );
  const matchesExpirationDate = matchesDateRangeFilter(
    assignedParkingRight.expirationDate,
    query.expirationDateStart,
    query.expirationDateEnd
  );

  return (
    matchesPlace &&
    matchesDescription &&
    matchesNumberPlate &&
    matchesProducts &&
    matchesAssignedDate &&
    matchesStartedDate &&
    matchesActivationDate &&
    matchesExpirationDate
  );
}

function reduceMyFleetVehiclesStateUpdate(
  state: VehicleAssignmentsMyFleetState,
  locatedParkingProducts: LocatedParkingProduct[],
  aggregateId: string,
  update: UpdateVehicleRequestModel
): VehicleAssignmentsMyFleetState {
  const createFakeProduct = (pmcId: number): VehicleAssignmentProduct => {
    const { name, location } = getProductInfo(locatedParkingProducts, pmcId);

    return {
      pmcId,
      locationName: location,
      pmcName: name,
      parkingRightId: `pr id for ${pmcId}`,
    };
  };

  let rowsAdded = 0;

  const newPages = mapValues(state.pages, (page) => {
    const rowIndex = page.data!.findIndex((r) => r.vehicleAssignmentId === aggregateId);

    if (rowIndex === -1) {
      const newFreshRow: VehicleAssignment = {
        vehicleAssignmentId: aggregateId,
        vehicle: {
          countryCode: update.numberPlate.countryCode,
          identifier: update.numberPlate.value,
          description: update.description,
        },
        products: update.parkingProducts.map(parkingProduct => createFakeProduct(parkingProduct.pmcId)),
        assignedAt: new Date(),
      };

      if (vehicleAssignmentMatchesQuery(newFreshRow, page.query)) {
        rowsAdded++;
        return {
          ...page,
          data: [newFreshRow, ...page.data!],
        };
      }

      return page;
    }
    const row = page.data![rowIndex];
    const productsToAdd: VehicleAssignmentProduct[] = update.parkingProducts
      .filter((updatePmc) => !row.products.some((rowProduct) => rowProduct.pmcId === updatePmc.pmcId))
      .map(parkingProduct => createFakeProduct(parkingProduct.pmcId));

    const updatedRow: VehicleAssignment = {
      ...row,
      vehicle: {
        ...row.vehicle,
        description: update.description,
      },
      products: row.products.filter((p) => update.parkingProducts.map(p => p.pmcId).includes(p.pmcId)).concat(productsToAdd),
    };

    if (!vehicleAssignmentMatchesQuery(updatedRow, page.query)) {
      return {
        ...page,
        data: page.data!.filter((_, index) => index !== rowIndex),
      };
    }

    return {
      ...page,
      data: page.data!.with(rowIndex, updatedRow),
    };
  });

  return {
    ...state,
    pages: newPages,
    totalRecords: state.totalRecords + rowsAdded
  };
}


function vehicleAssignmentMyFleetMatchesQuery(
  row: VehicleAssignmentMyFleet,
  query: GetVehicleAssignmentsMyFleetQuery
): boolean {
  const matchesDescription = matchesTextFilter(query.description, row.vehicle.description);
  const matchesNumberPlate = matchesTextFilter(query.numberPlateValue, row.vehicle.identifier);
  const matchesProducts =
    !hasQueryValue(query.pmcIds) ||
    (row.product !== null && query.pmcIds!.includes(row.product.pmcId));

  return matchesDescription && matchesNumberPlate && matchesProducts;
}

function reduceMyFleetOverviewVehiclesStateUpdate(
  state: VehicleAssignmentsMyFleetOverviewState,
  locatedParkingProducts: LocatedParkingProduct[],
  aggregateId: string,
  update: UpdateVehicleRequestModel
): VehicleAssignmentsMyFleetOverviewState {
  const createFakeProduct = (pmcId: number, activationDate?: string | null, expirationDate?: string | null): VehicleAssignmentProductMyFleet => {
    const { name, location } = getProductInfo(locatedParkingProducts, pmcId);

    return {
      pmcId,
      locationName: location,
      pmcName: name,
      parkingRightId: `pr id for ${pmcId}`,
      activationDate: activationDate,
      expirationDate: expirationDate,
    };
  };

  let rowsAdded = 0;
  let uniqueVehiclesAdded = 0;

  const newPages = mapValues(state.pages, (page) => {
    let pageData = [...page.data!];
    const vehicleAlreadyPresent = pageData.some((r) => r.vehicleAssignmentId === aggregateId);
    let newVehicleAddedToPage = false;

    // Upsert one row per product in the update
    for (const parkingProduct of update.parkingProducts) {
      const rowIndex = pageData.findIndex(
        (r) => r.vehicleAssignmentId === aggregateId && r.product?.pmcId === parkingProduct.pmcId
      );

      const fakeProduct = createFakeProduct(
        parkingProduct.pmcId, 
        parkingProduct.activationDate, 
        parkingProduct.expirationDate);

      if (rowIndex === -1) {
        const newRow: VehicleAssignmentMyFleet = {
          vehicleAssignmentId: aggregateId,
          createdAt: new Date(),
          vehicle: {
            countryCode: update.numberPlate.countryCode,
            identifier: update.numberPlate.value,
            description: update.description,
          },
          product: fakeProduct,
        };

        if (vehicleAssignmentMyFleetMatchesQuery(newRow, page.query)) {
          rowsAdded++;
          newVehicleAddedToPage = true;
          pageData = [newRow, ...pageData];
        }
      } else {
        const row = pageData[rowIndex];
        const updatedRow: VehicleAssignmentMyFleet = {
          ...row,
          vehicle: { ...row.vehicle, description: update.description },
          product: fakeProduct,
        };

        if (!vehicleAssignmentMyFleetMatchesQuery(updatedRow, page.query)) {
          pageData = pageData.filter((_, i) => i !== rowIndex);
        } else {
          pageData = pageData.with(rowIndex, updatedRow);
        }
      }
    }

    // Remove rows whose product is no longer in the update
    pageData = pageData.filter(
      (r) =>
        r.vehicleAssignmentId !== aggregateId ||
        update.parkingProducts.some((p) => p.pmcId === r.product?.pmcId)
    );

    if (!vehicleAlreadyPresent && newVehicleAddedToPage) {
      uniqueVehiclesAdded++;
    }

    return { ...page, data: pageData };
  });

  return {
    ...state,
    pages: newPages,
    totalRecords: state.totalRecords + rowsAdded,
    totalUniqueVehicles: state.totalUniqueVehicles + uniqueVehiclesAdded,
  };
}

function reduceMyProductsVehiclesState(
  vehicles: VehicleAssignmentMyProductsState,
  locatedParkingProducts: LocatedParkingProduct[],
  aggregateId: string,
  model: UpdateVehicleRequestModel,
  pmcToParkingRightStartDates?: Record<number, ParkingRightItemModel>
): VehicleAssignmentMyProductsState {
  const fakeRows: AssignedParkingRight[] = model.parkingProducts.map((parkingProduct) => {
    const { name, location } = getProductInfo(locatedParkingProducts, parkingProduct.pmcId);

    let startedAt = undefined as string | undefined;
    if(pmcToParkingRightStartDates){
      startedAt = pmcToParkingRightStartDates[parkingProduct.pmcId].parkingRightStartedDate
    }

    return createFakeAssignedParkingRight(
      aggregateId,
      model,
      parkingProduct.pmcId,
      name,
      location,
      startedAt,
      parkingProduct.activationDate,
      parkingProduct.expirationDate
    );
  });

  const newPagesWithAddedCount = mapValues(vehicles.pages, (page) => {
    const updatedRows = page
      .data!.filter(
        (p) => p.vehicleAssignmentId === aggregateId && model.parkingProducts.map(p => p.pmcId).includes(p.parkingRight.pmc)
      )
      .map((x) : AssignedParkingRight => {
        const parkingProduct = model.parkingProducts.find(p => p.pmcId === x.parkingRight.pmc);
        return {
          ...x,
          vehicle: {
            ...x.vehicle,
            description: model.description,
          },
          activationDate: parkingProduct?.activationDate ?? null,
          expirationDate: parkingProduct?.expirationDate ?? null,
        };
      });

    const rowsToAdd = fakeRows.filter(
      (row) =>
        !updatedRows.some(
          (updateRow) =>
            updateRow.vehicleAssignmentId === row.vehicleAssignmentId &&
            row.parkingRight.pmc === updateRow.parkingRight.pmc
        ) &&
        assignedParkingRightMatchesQuery(
          row,
          page.query,
          (pmc) => getProductInfo(locatedParkingProducts, pmc).placeId
        )
    );

    const rowsToRemove = page.data!.filter(
      (x) => x.vehicleAssignmentId === aggregateId && !model.parkingProducts.map(p => p.pmcId).includes(x.parkingRight.pmc)
    );

    const data = [
      ...rowsToAdd,
      ...page.data!.flatMap((row) => {
        if (rowsToRemove.includes(row)) {
          return [];
        }

        const updatedRow = updatedRows.find(
          (ur) =>
            ur.vehicleAssignmentId === row.vehicleAssignmentId &&
            ur.parkingRight.pmc === row.parkingRight.pmc
        );

        if (updatedRow) {
          return [updatedRow];
        }

        return [row];
      }),
    ];

    const newPage: typeof page = { ...page, data };
    return { page: newPage, rowsAddedCount: data.length - page.data!.length };
  });

  const newPages = mapValues(newPagesWithAddedCount, (x) => x.page);

  const newTotalRecords = vehicles.totalRecords + sumBy(Object.values(newPagesWithAddedCount), x => x.rowsAddedCount);
  return { ...vehicles, totalRecords: newTotalRecords, pages: newPages };
}

function createFakeAssignedParkingRight(
  aggregateId: string,
  assign: UpdateVehicleRequestModel,
  pmcId: number,
  name: string,
  location: string,
  startDate?: string,
  activationDate?: string | null,
  expirationDate?: string | null
): AssignedParkingRight {
  return {
    vehicleAssignmentId: aggregateId,
    parkingRight: {
      id: `fake pr id for pmc=${pmcId}`,
      pmc: pmcId,
      name,
      location,
    },
    assigned: new Date().toISOString(),
    vehicle: {
      countryCode: assign.numberPlate.countryCode,
      identifier: assign.numberPlate.value,
      description: assign.description,
    },
    startedAt: startDate || null,
    activationDate: activationDate ?? null,
    expirationDate: expirationDate ?? null,
  };
}

function reduceAssign(
  state: ParkingProductState,
  model: AssignVehicleRequestModel,
  pmcToParkingRightStartDates: Record<number, ParkingRightItemModel>
): ParkingProductState {
  const newLocatedParkingProducts = incrementTotalAssignedCount(
    state.locatedParkingProducts.data!,
    Object.fromEntries(model.parkingProducts.map((parkingProduct) => {
      if(parkingProduct.activationDate && new Date(parkingProduct.activationDate) > new Date()){
          return [parkingProduct.pmcId, 0];
      }
      return [parkingProduct.pmcId, 1];
    }),
  ));

  const newMyProductsVehicles = reduceMyProductsVehiclesState(
    state.vehiclesMyProducts,
    state.locatedParkingProducts.data!,
    model.aggregateId,
    model,
    pmcToParkingRightStartDates
  );

  const newMyFleetVehicles = reduceMyFleetVehiclesStateUpdate(
    state.vehicleAssignmentsMyFleet,
    state.locatedParkingProducts.data!,
    model.aggregateId,
    model
  );

  const newMyFleetOverviewVehicles = reduceMyFleetOverviewVehiclesStateUpdate(
    state.vehicleAssignmentsMyFleetOverview,
    state.locatedParkingProducts.data!,
    model.aggregateId,
    model
  );

  return {
    ...state,
    locatedParkingProducts: {
      ...state.locatedParkingProducts,
      data: newLocatedParkingProducts,
    },
    vehiclesMyProducts: newMyProductsVehicles,
    vehicleAssignmentsMyFleet: newMyFleetVehicles,
    vehicleAssignmentsMyFleetOverview: newMyFleetOverviewVehicles,
  };
}

function getProductInfo(
  locations: LocatedParkingProduct[],
  pmcId: number
): { location: string; name: string; placeId: number } {
  for (const location of locations) {
    for (const product of location.parkingProducts) {
      if (product.pmcId === pmcId) {
        return {
          location: product.location,
          name: product.pmcName,
          placeId: location.placeId,
        };
      }
    }
  }

  throw new Error(`Can't find product info for pmc ${pmcId}`);
}

export function reduceVehicleAssignmentMutation(
  state: ParkingProductState,
  mutation: AssignedVehicleSuccessfulMutation
): ParkingProductState {
  switch (mutation.type) {
    case "update":
      return reduceUpdate(state, mutation.aggregateId, mutation.request, mutation.pmcToParkingRightStartDates);
    case "assign":
      return reduceAssign(state, mutation.request, mutation.pmcToParkingRightStartDates);
  }
}

export function reduceDeleteLocatedParkingProducts(
  state: ParkingProductState,
  vehicleAssignmentIds: string[]
): ParkingProductState {
  if (!state.locatedParkingProducts.data || vehicleAssignmentIds.length === 0) {
    return state;
  }

  const pmcToDecrement: Record<number, number> = {};
  for (const page of Object.values(state.vehiclesMyProducts.pages ?? {})) {
    for (const row of page.data ?? []) {
      if (vehicleAssignmentIds.includes(row.vehicleAssignmentId)) {
        if (row.activationDate && new Date(row.activationDate) > new Date()) {
          continue;
        }
        const pmc = row.parkingRight.pmc;
        pmcToDecrement[pmc] = (pmcToDecrement[pmc] ?? 0) - 1;
      }
    }
  }

  if (Object.keys(pmcToDecrement).length === 0) {
    return state;
  }

  return {
    ...state,
    locatedParkingProducts: {
      ...state.locatedParkingProducts,
      data: incrementTotalAssignedCount(state.locatedParkingProducts.data, pmcToDecrement),
    },
  };
}

export function reduceRevokeMyFleet(
  state: VehicleAssignmentsMyFleetState,
  revokeModel: RevokeVehicleAssignmentsModel
): VehicleAssignmentsMyFleetState {
  const newPages = mapValues(state.pages, (page) => {
    const newData = page.data!.map((vehicle) => {
      const productsToRemove = vehicle.products.filter((vp) =>
        revokeModel.vehicleAssignmentProducts.some(
          (vap) => vap.vehicleAssignmentId === vehicle.vehicleAssignmentId && vap.pmcId === vp.pmcId
        )
      );
      if (productsToRemove.length === 0) {
        return vehicle;
      }
      return {
        ...vehicle,
        products: vehicle.products.filter((x) => !productsToRemove.includes(x)),
      };
    });
    return { ...page, data: newData };
  });

  return {
    ...state,
    pages: newPages,
  };
}

export function reduceRevokeMyFleetOverview(
  state: VehicleAssignmentsMyFleetOverviewState,
  revokeModel: RevokeVehicleAssignmentsModel
): VehicleAssignmentsMyFleetOverviewState {
  const newPages = mapValues(state.pages, (page) => {
    const vehicleRowCounts = page.data!.reduce((acc, vehicle) => {
      acc[vehicle.vehicleAssignmentId] = (acc[vehicle.vehicleAssignmentId] ?? 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const newData = page.data!.flatMap((vehicle) => {
      if (vehicle.product === null) {
        return [vehicle];
      }

      const revokedProduct = revokeModel.vehicleAssignmentProducts.find(
        (vap) => vap.vehicleAssignmentId === vehicle.vehicleAssignmentId && vap.pmcId === vehicle.product?.pmcId
      );

      if (!revokedProduct) {
        return [vehicle];
      }

      const rowCount = vehicleRowCounts[vehicle.vehicleAssignmentId] ?? 0;
      if (rowCount <= 1) {
        return [
          {
            ...vehicle,
            product: null,
          },
        ];
      }

      return [];
    });
    return { ...page, data: newData };
  });

  return {
    ...state,
    pages: newPages,
  };
}
